import React from 'react';
import styled from '@emotion/styled';
import BasePopover, { PopoverProps as BasePopoverProps } from 'react-popover';

import colours from '~/utils/colours';
import ReplaceableComponents from '~/components/higher-order/ReplaceableComponents';

export const theme = {
  bg: 'white',
  borderColor: colours.greyMedium,
};

export enum Alignment {
  Left = 'left',
  Right = 'right',
  Middle = 'middle',
  Bottom = 'bottom',
}

interface AlignmentProp {
  align?: Alignment | null;
  leftOffset?: string;
}

function getAlignment({ align, leftOffset }: AlignmentProp) {
  if (leftOffset) return leftOffset;

  switch (align) {
    case Alignment.Left:
      return '25%';
    case Alignment.Right:
      return '-25%';
    case Alignment.Middle:
    default:
      return '0';
  }
}

interface PositionProp {
  place?: BasePopoverProps['place'];
}

function getPositionProp({ place }: PositionProp) {
  switch (place) {
    case 'above':
      return 'bottom';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    case 'below':
    default:
      return 'top';
  }
}

export interface PopoverTheme {
  borderColor: string;
  bg: string;
}

interface StyledPopoverProps extends AlignmentProp, PositionProp {
  tipColour?: string | null;
  theme: PopoverTheme;
  hideArrow?: boolean;
}

const StyledPopover = styled(BasePopover)<
  StyledPopoverProps & BasePopoverProps
>`
  z-index: 9990;

  .Popover-body {
    position: relative;
    ${({ place }: StyledPopoverProps) => getPositionProp({ place })}: 2px;
    left: ${({ align, leftOffset }: StyledPopoverProps) =>
      getAlignment({ align, leftOffset })};
  }

  .Popover-tip {
    ${({ place }: StyledPopoverProps) => getPositionProp({ place })}: -3px;
    z-index: 1000;
    position: absolute;
    stroke-dasharray: 18, 7, 0;
    stroke: ${({ theme }: StyledPopoverProps) => theme.borderColor};
    fill: ${({ tipColour, theme }: StyledPopoverProps) =>
      tipColour || theme.bg};
    display: ${({ hideArrow }: StyledPopoverProps) =>
      hideArrow ? 'none' : 'block'};
  }
`;

const BodyContainer = styled.div`
  background-color: transparent;
`;

export const defaultComponents = {
  BodyContainer,
  Popover: StyledPopover,
};

export type PopoverProps = {
  components: typeof defaultComponents;
  align: Alignment;
  theme: PopoverTheme;
  tipColour: string;
  tipSize: number;
  leftOffset?: string;
} & BasePopoverProps;

@ReplaceableComponents(defaultComponents)
class Popover extends React.Component<PopoverProps> {
  static defaultProps: Partial<PopoverProps> = {
    theme,
    tipSize: 6,
  };

  render() {
    const { children, components } = this.props;
    const { Popover } = components ?? defaultComponents;

    return <Popover {...this.getPopoverProps()}>{children}</Popover>;
  }

  getPopoverProps() {
    // eslint-disable-next-line no-unused-vars
    const { components, body, className, ...props } = this.props;

    return {
      body: this.renderPopoverBody(),
      appendTarget: document.body,
      ...props,
      refreshIntervalMs: 0,
    };
  }

  renderPopoverBody() {
    const { components, body, className } = this.props;

    const { BodyContainer } = components ?? defaultComponents;

    return <BodyContainer className={className}>{body}</BodyContainer>;
  }
}

export default Popover;
